import React, { useEffect, useMemo, useState } from "react";
import { X, Sparkles, Loader2, AlertTriangle, CalendarRange, CalendarDays } from "lucide-react";
import { birpApi } from "../api/birpApi";
import DatePickerUS from "@/components/DatePickerUS";

const MAX_PERIOD_DAYS = 90;

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (iso, days) => {
    const d = new Date(iso + "T00:00:00");
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
};
const diffDaysInclusive = (a, b) => {
    if (!a || !b) return 0;
    const da = new Date(a + "T00:00:00");
    const db = new Date(b + "T00:00:00");
    return Math.round((db - da) / 86400000) + 1;
};

const BirpGenerateDialog = ({
    open,
    onOpenChange,
    individuals,
    defaultIndividualId,
    onGenerate,
    generating,
}) => {
    const [individualId, setIndividualId] = useState(
        defaultIndividualId || individuals[0]?.id || "",
    );
    const [mode, setMode] = useState("day"); // "day" | "range"
    const [reportDate, setReportDate] = useState(todayISO());
    const [periodEnd, setPeriodEnd] = useState(todayISO());
    const [force, setForce] = useState(false);
    const [allowEmpty, setAllowEmpty] = useState(false);
    const [datesInfo, setDatesInfo] = useState(null);
    const [datesLoading, setDatesLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setIndividualId(defaultIndividualId || individuals[0]?.id || "");
            setMode("day");
            setReportDate(todayISO());
            setPeriodEnd(todayISO());
            setForce(false);
            setAllowEmpty(false);
            setDatesInfo(null);
        }
    }, [open, defaultIndividualId, individuals]);

    // Load recent dates-with-data when the individual changes
    useEffect(() => {
        if (!open || !individualId) return;
        setDatesLoading(true);
        birpApi
            .datesWithData(individualId, 90)
            .then(setDatesInfo)
            .catch(() => setDatesInfo([]))
            .finally(() => setDatesLoading(false));
    }, [open, individualId]);

    // Keep periodEnd >= reportDate when the mode is "range"
    useEffect(() => {
        if (mode === "range" && periodEnd < reportDate) {
            setPeriodEnd(reportDate);
        }
    }, [mode, reportDate, periodEnd]);

    const countForRange = useMemo(() => {
        if (!datesInfo) return null;
        if (mode === "day") {
            const hit = datesInfo.find((d) => d.date === reportDate);
            return hit ? hit.measurement_count : 0;
        }
        const start = reportDate;
        const end = periodEnd;
        return datesInfo
            .filter((d) => d.date >= start && d.date <= end)
            .reduce((sum, d) => sum + (d.measurement_count || 0), 0);
    }, [datesInfo, mode, reportDate, periodEnd]);

    const suggestions = useMemo(
        () => (datesInfo || []).slice(0, 3),
        [datesInfo],
    );

    const spanDays = mode === "range" ? diffDaysInclusive(reportDate, periodEnd) : 1;
    const rangeInvalid = mode === "range" && periodEnd < reportDate;
    const rangeTooLong = mode === "range" && spanDays > MAX_PERIOD_DAYS;

    if (!open) return null;

    const isEmpty = datesInfo != null && countForRange === 0;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!individualId || !reportDate) return;
        if (mode === "range" && (rangeInvalid || rangeTooLong)) return;
        onGenerate({
            individual_id: individualId,
            report_date: reportDate,
            period_end: mode === "range" ? periodEnd : null,
            force,
            allow_empty: allowEmpty,
        });
    };

    return (
        <div
            className="fixed inset-0 z-40 flex items-end justify-center bg-slate-900/50 p-2 backdrop-blur-sm sm:items-center sm:p-4"
            data-testid="birp-generate-dialog"
        >
            <form
                onSubmit={handleSubmit}
                className="flex max-h-[95vh] w-full max-w-md flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl"
            >
                <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                    <h3 className="text-base font-heading font-bold text-slate-900">
                        Generate Individual BIRP Report
                    </h3>
                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        data-testid="birp-generate-close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="min-w-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
                    <div>
                        <label className="mb-1 block text-[11px] font-bold uppercase tracking-widest text-slate-500">
                            Individual
                        </label>
                        <select
                            value={individualId}
                            onChange={(e) => setIndividualId(e.target.value)}
                            data-testid="birp-generate-individual"
                            className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/30"
                        >
                            {individuals.map((i) => (
                                <option key={i.id} value={i.id}>
                                    {`${i.first_name} ${i.last_name}`}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Mode toggle: single day / date range */}
                    <div>
                        <label className="mb-1 block text-[11px] font-bold uppercase tracking-widest text-slate-500">
                            Coverage
                        </label>
                        <div
                            role="tablist"
                            className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1"
                            data-testid="birp-period-toggle"
                        >
                            <button
                                type="button"
                                role="tab"
                                aria-selected={mode === "day"}
                                onClick={() => setMode("day")}
                                data-testid="birp-mode-day"
                                data-active={mode === "day" || undefined}
                                className={`inline-flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-bold transition-colors ${
                                    mode === "day"
                                        ? "bg-white text-brand-blue-700 shadow-sm"
                                        : "text-slate-500 hover:text-slate-700"
                                }`}
                            >
                                <CalendarDays className="h-3.5 w-3.5" />
                                Single day
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={mode === "range"}
                                onClick={() => setMode("range")}
                                data-testid="birp-mode-range"
                                data-active={mode === "range" || undefined}
                                className={`inline-flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-bold transition-colors ${
                                    mode === "range"
                                        ? "bg-white text-brand-blue-700 shadow-sm"
                                        : "text-slate-500 hover:text-slate-700"
                                }`}
                            >
                                <CalendarRange className="h-3.5 w-3.5" />
                                Date range
                            </button>
                        </div>
                    </div>

                    {/* Date inputs */}
                    {mode === "day" ? (
                        <div>
                            <label className="mb-1 block text-[11px] font-bold uppercase tracking-widest text-slate-500">
                                Report date
                            </label>
                            <DatePickerUS
                                value={reportDate}
                                onChange={(iso) => iso && setReportDate(iso)}
                                testId="birp-generate-date"
                                className="h-10 w-full"
                            />
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="mb-1 block text-[11px] font-bold uppercase tracking-widest text-slate-500">
                                    Start date
                                </label>
                                <DatePickerUS
                                    value={reportDate}
                                    onChange={(iso) => iso && setReportDate(iso)}
                                    testId="birp-range-start"
                                    className="h-10 w-full"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-[11px] font-bold uppercase tracking-widest text-slate-500">
                                    End date
                                </label>
                                <DatePickerUS
                                    value={periodEnd}
                                    onChange={(iso) => iso && setPeriodEnd(iso)}
                                    min={reportDate}
                                    max={addDaysISO(reportDate, MAX_PERIOD_DAYS - 1)}
                                    testId="birp-range-end"
                                    className="h-10 w-full"
                                />
                            </div>
                        </div>
                    )}

                    {/* Availability + validation hints */}
                    {mode === "range" && !rangeInvalid && !rangeTooLong && (
                        <p
                            className="text-[11px] font-semibold text-slate-500"
                            data-testid="birp-range-span"
                        >
                            {spanDays} {spanDays === 1 ? "day" : "days"} selected · max {MAX_PERIOD_DAYS}
                        </p>
                    )}
                    {rangeInvalid && (
                        <p
                            className="text-[11px] font-bold text-red-700"
                            data-testid="birp-range-error"
                        >
                            End date must be on or after start date.
                        </p>
                    )}
                    {rangeTooLong && (
                        <p
                            className="text-[11px] font-bold text-red-700"
                            data-testid="birp-range-toolong"
                        >
                            Range too long ({spanDays} days). Maximum is {MAX_PERIOD_DAYS} days.
                        </p>
                    )}

                    {datesLoading && (
                        <p className="text-[11px] text-slate-400">
                            Checking measurement availability…
                        </p>
                    )}
                    {!datesLoading && countForRange !== null && countForRange > 0 && (
                        <p
                            className="text-[11px] font-semibold text-brand-green-700"
                            data-testid="birp-date-count"
                        >
                            {countForRange}{" "}
                            {countForRange === 1 ? "measurement" : "measurements"}{" "}
                            {mode === "range" ? "in this period" : "on this date"}
                        </p>
                    )}
                    {!datesLoading && isEmpty && (
                        <div
                            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
                            data-testid="birp-date-empty-warning"
                        >
                            <div className="flex items-center gap-1.5 text-[11px] font-bold text-amber-800">
                                <AlertTriangle className="h-3.5 w-3.5" />
                                No measurements {mode === "range" ? "in this period" : "on this date"}
                            </div>
                            {suggestions.length > 0 && (
                                <>
                                    <p className="mt-1 text-[11px] text-amber-800">
                                        Nearby dates with data:
                                    </p>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {suggestions.map((s) => (
                                            <button
                                                key={s.date}
                                                type="button"
                                                onClick={() => {
                                                    setReportDate(s.date);
                                                    if (mode === "range") setPeriodEnd(s.date);
                                                }}
                                                data-testid={`birp-date-suggest-${s.date}`}
                                                className="rounded-md border border-amber-300 bg-white px-2 py-0.5 text-[10px] font-bold text-amber-900 hover:bg-amber-100"
                                            >
                                                {s.date} · {s.measurement_count}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                            <label className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-900">
                                <input
                                    type="checkbox"
                                    checked={allowEmpty}
                                    onChange={(e) => setAllowEmpty(e.target.checked)}
                                    data-testid="birp-generate-allow-empty"
                                    className="mt-0.5 h-3.5 w-3.5"
                                />
                                <span>
                                    Generate anyway — Claude will state
                                    &quot;no observations recorded&quot;
                                    instead of fabricating behaviors.
                                </span>
                            </label>
                        </div>
                    )}

                    <label className="flex items-center gap-2 text-xs text-slate-600">
                        <input
                            type="checkbox"
                            checked={force}
                            onChange={(e) => setForce(e.target.checked)}
                            data-testid="birp-generate-force"
                            className="h-4 w-4"
                        />
                        Regenerate even if an in-review/approved draft exists (force)
                    </label>
                    <p className="rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
                        Claude generates 4 BIRP sections using the measurements
                        {mode === "range" ? " within the selected period" : " on the selected day"} and
                        the active plan for this individual.
                    </p>
                </div>
                <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/60 px-5 py-3">
                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        className="rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                        data-testid="birp-generate-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={
                            generating ||
                            !individualId ||
                            !reportDate ||
                            rangeInvalid ||
                            rangeTooLong ||
                            (isEmpty && !allowEmpty)
                        }
                        data-testid="birp-generate-submit"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-brand-blue-700 px-4 py-1.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-brand-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {generating ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Sparkles className="h-4 w-4" />
                        )}
                        {generating ? "Generating…" : "Generate"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default BirpGenerateDialog;
